import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from "react";
import { collection, doc, onSnapshot, type QuerySnapshot } from "firebase/firestore";
import { AppState } from "react-native";

import {
  firestorePaths,
  PLAYBACK_SESSION_HEARTBEAT_SECONDS,
  type DancePlaybackGrant,
  type PlaybackItemRef,
  type PlaybackMode,
  type PlaybackNowPlaying,
  type PlaybackQueueScope,
  type PlaybackSession,
  type PlaybackVideoSources
} from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import { useAppRouter } from "@/lib/appRouter";
import { StreamTakeoverDialog } from "@/components/dialogs";
import { getClientDeviceIdentity } from "@/devices/deviceIdentity";
import {
  createPlaybackSession,
  endPlaybackSession,
  firebaseErrorCode,
  firebaseErrorMessage,
  heartbeatPlaybackSession,
  refreshPlayback
} from "@/firebase/callables";
import { firestore, hasFirebaseClientConfig } from "@/firebase/client";
import { useTerminatePlaybackOnAuthSessionChange } from "@/playback/authPlaybackLifecycle";
import { usesProtectedPlayback } from "@/playback/playbackAccessMode";
import { setCachedPlaybackGrants } from "@/playback/playbackGrantCache";

type PlaybackSessionState = {
  authorizationStatus: "idle" | "requesting" | "device-denied" | "error" | "ready";
  expiresAtForDance: (danceId: string) => number | null;
  freshSourceForDance: (danceId: string) => Promise<string | null>;
  sourceForDance: (danceId: string) => string | null;
  videoSourcesForDance: (danceId: string) => PlaybackVideoSources | null;
  storyboardForDance: (danceId: string) => string | null;
  displaced: boolean;
  displacedSession: DisplacedSessionMetadata | null;
  reportPlaybackActivity: (active: boolean) => void;
  updateNowPlaying: (nowPlaying: PlaybackNowPlaying) => void;
  takeOverCurrentPlayback: () => Promise<void>;
  takingOver: boolean;
  startQueue: (request: {
    queueIdentity: string;
    recitalId: string;
    scope: PlaybackQueueScope;
    mode: PlaybackMode;
    items: PlaybackItemRef[];
    nowPlaying: PlaybackNowPlaying;
  }) => Promise<void>;
  stopQueue: () => void;
  unavailableMessage: string | null;
  revoked: boolean;
  receiverBadge: string | null;
  sessionId: string | null;
};

type ActivePlaybackRequest = {
  queueIdentity: string;
  recitalId: string;
  scope: PlaybackQueueScope;
  mode: PlaybackMode;
  items: PlaybackItemRef[];
  nowPlaying: PlaybackNowPlaying;
};

type StartPlaybackRequest = ActivePlaybackRequest & {
  takeOver?: boolean;
};

const HEARTBEAT_SUCCESS_LOG_INTERVAL_MS = 5 * 60 * 1000;
const PLAYBACK_STALL_TIMEOUT_MS = 20_000;
const SESSION_RECOVERY_DELAYS_MS = [1_000, 3_000, 10_000, 30_000] as const;
const GRANT_REFRESH_RETRY_MS = 5_000;

function isRetryableSessionError(error: unknown): boolean {
  const code = firebaseErrorCode(error);
  return (
    code == null ||
    code === "cancelled" ||
    code === "deadline-exceeded" ||
    code === "internal" ||
    code === "unknown" ||
    code === "unauthenticated" ||
    code === "unavailable"
  );
}

export type DisplacedSessionMetadata = Pick<
  PlaybackSession,
  "sessionId" | "deviceId" | "deviceLabel" | "deviceFormFactor" | "nowPlaying"
>;

function samePlaybackRequest(
  left: ActivePlaybackRequest | null,
  right: ActivePlaybackRequest
): boolean {
  return Boolean(
    left &&
      left.queueIdentity === right.queueIdentity &&
      left.recitalId === right.recitalId &&
      left.scope.kind === right.scope.kind &&
      left.scope.id === right.scope.id &&
      left.mode === right.mode &&
      left.items.length === right.items.length &&
      left.items.every(
        (item, index) =>
          item.performanceId === right.items[index]?.performanceId &&
          item.danceId === right.items[index]?.danceId
      )
  );
}

const PlaybackSessionContext = createContext<PlaybackSessionState | null>(null);

function mapGrants(dances: DancePlaybackGrant[]): Record<string, string> {
  return Object.fromEntries(dances.map((grant) => [grant.danceId, grant.playbackUrl]));
}

function mapVideoSources(
  dances: DancePlaybackGrant[]
): Record<string, PlaybackVideoSources> {
  return Object.fromEntries(
    dances.flatMap((grant) =>
      grant.videoSources ? [[grant.danceId, grant.videoSources] as const] : []
    )
  );
}

function mapStoryboardGrants(dances: DancePlaybackGrant[]): Record<string, string> {
  return Object.fromEntries(dances.map((grant) => [grant.danceId, grant.storyboardVttUrl]));
}

function mapGrantExpiries(dances: DancePlaybackGrant[]): Record<string, number> {
  return Object.fromEntries(
    dances
      .map((grant) => [grant.danceId, Date.parse(grant.expiresAt)] as const)
      .filter((entry): entry is readonly [string, number] => Number.isFinite(entry[1]))
  );
}

function findActivePlaybackSession(snapshot: QuerySnapshot): DisplacedSessionMetadata | null {
  const now = Date.now();
  const active = snapshot.docs
    .filter((entry) => {
      const expiresAt = Date.parse(String(entry.get("expiresAt") ?? ""));
      return entry.get("revokedAt") == null && Number.isFinite(expiresAt) && expiresAt > now;
    })
    .sort((left, right) =>
      String(right.get("createdAt") ?? "").localeCompare(String(left.get("createdAt") ?? ""))
    )[0];
  const deviceId = active?.get("deviceId");
  if (typeof deviceId !== "string") return null;
  const sessionId = active?.get("sessionId");
  return {
    sessionId: typeof sessionId === "string" ? sessionId : active.id,
    deviceId,
    deviceLabel: active.get("deviceLabel") as string,
    deviceFormFactor: active.get(
      "deviceFormFactor"
    ) as PlaybackSession["deviceFormFactor"],
    nowPlaying: active.get("nowPlaying") as PlaybackNowPlaying
  };
}

export function PlaybackSessionProvider({ children }: PropsWithChildren) {
  const router = useAppRouter();
  const {
    demoMode,
    device,
    deviceActivationComplete,
    deviceLimitReached,
    authBootstrapComplete,
    user
  } = useAuthState();
  const localPlaybackMode = !usesProtectedPlayback({
    firebaseConfigured: hasFirebaseClientConfig,
    demoMode
  });
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [videoSources, setVideoSources] = useState<Record<string, PlaybackVideoSources>>({});
  const [storyboardUrls, setStoryboardUrls] = useState<Record<string, string>>({});
  const [grantExpiries, setGrantExpiries] = useState<Record<string, number>>({});
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [receiverBadge, setReceiverBadge] = useState<string | null>(null);
  const [activeRequest, setActiveRequest] = useState<ActivePlaybackRequest | null>(null);
  const [streamInUseLabel, setStreamInUseLabel] = useState<string | null | undefined>();
  const [revoked, setRevoked] = useState(false);
  const [displaced, setDisplaced] = useState(false);
  const [displacedSession, setDisplacedSession] =
    useState<DisplacedSessionMetadata | null>(null);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);
  const [authorizationStatus, setAuthorizationStatus] =
    useState<PlaybackSessionState["authorizationStatus"]>("idle");
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRecoveryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const revokedRef = useRef(false);
  const nowPlayingRef = useRef<PlaybackNowPlaying | null>(null);
  const displacedListenerRef = useRef<(() => void) | null>(null);
  const activeRequestRef = useRef<ActivePlaybackRequest | null>(null);
  const playbackActiveRef = useRef(false);
  const playbackInactiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const publishedPlaybackActiveRef = useRef<{sessionId: string; active: boolean} | null>(null);
  const deviceRef = useRef(device);
  const deviceLimitReachedRef = useRef(deviceLimitReached);
  const retryableSessionFailureRef = useRef(false);
  const sessionRecoveryAttemptRef = useRef(0);
  const startQueueRef = useRef<(request: StartPlaybackRequest) => Promise<void>>(
    async () => {}
  );
  const lastHeartbeatSuccessLogRef = useRef<{ sessionId: string | null; at: number }>({
    sessionId: null,
    at: 0
  });
  deviceRef.current = device;
  deviceLimitReachedRef.current = deviceLimitReached;
  // Invalidates async grant responses when a player closes or a newer queue
  // replaces it. Without this, returning to the same dance can briefly reuse
  // its old URL before the new session response swaps in a fresh signed URL.
  const requestGeneration = useRef(0);

  const clearRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
  }, []);

  const clearSessionRecovery = useCallback(() => {
    if (sessionRecoveryTimer.current) clearTimeout(sessionRecoveryTimer.current);
    sessionRecoveryTimer.current = null;
  }, []);

  const clearDisplacedListener = useCallback(() => {
    displacedListenerRef.current?.();
    displacedListenerRef.current = null;
  }, []);

  const clearPlaybackInactiveTimer = useCallback(() => {
    if (playbackInactiveTimerRef.current) clearTimeout(playbackInactiveTimerRef.current);
    playbackInactiveTimerRef.current = null;
  }, []);

  const publishPlaybackActivity = useCallback(
    (active: boolean) => {
      playbackActiveRef.current = active;
      const activeSessionId = sessionIdRef.current;
      if (!activeSessionId || localPlaybackMode) return;
      const published = publishedPlaybackActiveRef.current;
      if (published?.sessionId === activeSessionId && published.active === active) return;
      publishedPlaybackActiveRef.current = {sessionId: activeSessionId, active};
      void heartbeatPlaybackSession(
        activeSessionId,
        nowPlayingRef.current ?? undefined,
        active
      ).catch(() => {
        // The regular heartbeat repairs a dropped presence transition.
        publishedPlaybackActiveRef.current = null;
      });
    },
    [localPlaybackMode]
  );

  const reportPlaybackActivity = useCallback(
    (active: boolean) => {
      clearPlaybackInactiveTimer();
      if (active) {
        playbackInactiveTimerRef.current = setTimeout(
          () => publishPlaybackActivity(false),
          PLAYBACK_STALL_TIMEOUT_MS
        );
      }
      publishPlaybackActivity(active);
    },
    [clearPlaybackInactiveTimer, publishPlaybackActivity]
  );

  const resolveDisplacedDevice = useCallback(
    (generation: number, assumeDisplaced = false) => {
      if (!firestore || !user) return;
      clearDisplacedListener();
      displacedListenerRef.current = onSnapshot(
        collection(firestore, `familyAccounts/${user.uid}/sessions`),
        (snapshot) => {
          if (generation !== requestGeneration.current) return;
          const session = findActivePlaybackSession(snapshot);
          setDisplacedSession(session);
          if (assumeDisplaced || session) setDisplaced(true);
        },
        () => {
          if (generation === requestGeneration.current && assumeDisplaced) {
            setDisplaced(true);
          }
        }
      );
    },
    [clearDisplacedListener, user]
  );

  const scheduleRefresh = useCallback(
    (
      nextSessionId: string,
      items: PlaybackItemRef[],
      grants: DancePlaybackGrant[],
      generation: number,
      retryDelayMs?: number
    ) => {
      clearRefresh();
      const firstExpiry = grants
        .map((grant) => new Date(grant.expiresAt).getTime())
        .sort((a, b) => a - b)[0];
      if (!firstExpiry) return;
      const ttl = Math.max(30_000, firstExpiry - Date.now());
      if (__DEV__) {
        console.log(
          `[playback-session] grant refresh scheduled in ${Math.floor(ttl * 0.75 / 1000)}s appState=${AppState.currentState}`
        );
      }
      refreshTimer.current = setTimeout(async () => {
        if (generation !== requestGeneration.current) return;
        try {
          const response = await refreshPlayback({ sessionId: nextSessionId, items });
          if (generation !== requestGeneration.current) return;
          if (response.status === "session-revoked") {
            setReceiverBadge(null);
            revokedRef.current = true;
            setRevoked(true);
            resolveDisplacedDevice(generation);
            return;
          }
          if (response.status === "session-expired") {
            sessionIdRef.current = null;
            setSessionId(null);
            setReceiverBadge(null);
            const retryRequest = activeRequestRef.current;
            if (retryRequest) void startQueueRef.current(retryRequest);
            return;
          }
          setUrls((current) => ({ ...current, ...mapGrants(response.dances) }));
          setVideoSources((current) => ({
            ...current,
            ...mapVideoSources(response.dances)
          }));
          setStoryboardUrls((current) => ({ ...current, ...mapStoryboardGrants(response.dances) }));
          setGrantExpiries((current) => ({ ...current, ...mapGrantExpiries(response.dances) }));
          setCachedPlaybackGrants(response.dances);
          if (response.receiverBadge) setReceiverBadge(response.receiverBadge);
          if (__DEV__) {
            console.log(
              `[playback-session] grant refresh ok dances=${response.dances.length} appState=${AppState.currentState}`
            );
          }
          scheduleRefresh(nextSessionId, items, response.dances, generation);
        } catch (error) {
          if (__DEV__) {
            console.log(
              `[playback-session] grant refresh failed appState=${AppState.currentState}: ${firebaseErrorMessage(error)}`
            );
          }
          if (firebaseErrorCode(error) === "permission-denied") {
            setUnavailableMessage(firebaseErrorMessage(error));
            return;
          }
          if (!isRetryableSessionError(error)) return;
          // A frozen browser can resume before its network stack is actually
          // usable. Keep the current signed grants and retry their refresh;
          // never turn that wake-up race into a terminal recital error.
          scheduleRefresh(
            nextSessionId,
            items,
            grants,
            generation,
            GRANT_REFRESH_RETRY_MS
          );
        }
      }, retryDelayMs ?? Math.floor(ttl * 0.75));
    },
    [clearRefresh, resolveDisplacedDevice]
  );

  const stopQueue = useCallback(() => {
    const endingSessionId = sessionIdRef.current;
    sessionIdRef.current = null;
    requestGeneration.current += 1;
    clearRefresh();
    clearSessionRecovery();
    clearDisplacedListener();
    setUrls({});
    setVideoSources({});
    setStoryboardUrls({});
    setGrantExpiries({});
    setSessionId(null);
    setReceiverBadge(null);
    activeRequestRef.current = null;
    nowPlayingRef.current = null;
    setActiveRequest(null);
    setStreamInUseLabel(undefined);
    revokedRef.current = false;
    setRevoked(false);
    setDisplaced(false);
    setDisplacedSession(null);
    setUnavailableMessage(null);
    setAuthorizationStatus("idle");
    retryableSessionFailureRef.current = false;
    sessionRecoveryAttemptRef.current = 0;
    healedRetryKey.current = null;
    clearPlaybackInactiveTimer();
    playbackActiveRef.current = false;
    publishedPlaybackActiveRef.current = null;
    if (endingSessionId && !localPlaybackMode) {
      void endPlaybackSession(endingSessionId).catch((error) => {
        if (__DEV__) console.log(`[playback] session end failed: ${firebaseErrorMessage(error)}`);
      });
    }
  }, [clearDisplacedListener, clearPlaybackInactiveTimer, clearRefresh, clearSessionRecovery, localPlaybackMode]);

  useTerminatePlaybackOnAuthSessionChange(stopQueue);

  // A takeover revokes this document in the same backend transaction that
  // creates the replacement session. Watch it in real time so the displaced
  // player stops immediately instead of continuing on its still-valid signed
  // URLs until the next scheduled grant refresh.
  useEffect(() => {
    if (!firestore || !user || !sessionId) return;
    const generation = requestGeneration.current;
    return onSnapshot(
      doc(firestore, firestorePaths.session(user.uid, sessionId)),
      (snapshot) => {
        if (generation !== requestGeneration.current) return;
        if (!snapshot.exists() || snapshot.get("revokedAt") == null) return;

        // Invalidate any refresh already in flight before removing the grants.
        requestGeneration.current += 1;
        const revokedGeneration = requestGeneration.current;
        clearRefresh();
        setUrls({});
        setVideoSources({});
        setStoryboardUrls({});
        setGrantExpiries({});
        sessionIdRef.current = null;
        setSessionId(null);
        setReceiverBadge(null);
        revokedRef.current = true;
        setRevoked(true);
        const superseded = snapshot.get("revokedReason") === "superseded";
        setDisplaced(superseded);
        setDisplacedSession(null);
        if (superseded) resolveDisplacedDevice(revokedGeneration, true);
      },
      () => {
        // The existing signed-grant refresh remains a fallback if the realtime
        // connection is temporarily unavailable.
      }
    );
  }, [clearRefresh, resolveDisplacedDevice, sessionId, user]);

  const startQueue = useCallback(
    async ({
      queueIdentity,
      recitalId,
      scope,
      mode,
      items,
      nowPlaying,
      takeOver = false
    }: StartPlaybackRequest) => {
      if (localPlaybackMode || !hasFirebaseClientConfig || items.length === 0) {
        setAuthorizationStatus("ready");
        return;
      }
      if (!authBootstrapComplete) return;
      if (!deviceActivationComplete) return;
      if (revokedRef.current && !takeOver) {
        if (__DEV__) {
          console.log("[playback-session] automatic start blocked after revocation");
        }
        return;
      }
      const nextRequest = {
        queueIdentity,
        recitalId,
        scope,
        mode,
        items,
        nowPlaying
      };
      if (sessionIdRef.current && samePlaybackRequest(activeRequestRef.current, nextRequest)) {
        setAuthorizationStatus("ready");
        return;
      }
      activeRequestRef.current = nextRequest;
      nowPlayingRef.current = nowPlaying;
      setActiveRequest(nextRequest);
      if (deviceLimitReachedRef.current) {
        setAuthorizationStatus("device-denied");
        return;
      }

      setAuthorizationStatus("requesting");
      clearSessionRecovery();
      // Foreground browsers can emit visibility, focus, and pageshow in one
      // burst. Claim the retry synchronously so those signals cannot create
      // overlapping playback sessions before React publishes "requesting".
      retryableSessionFailureRef.current = false;

      const generation = requestGeneration.current + 1;
      requestGeneration.current = generation;
      clearRefresh();
      // Never let a newly mounted player consume grants left by the previous
      // screen. The poster veil stays up until this request supplies its own.
      setUrls({});
      setVideoSources({});
      setStoryboardUrls({});
      setGrantExpiries({});

      try {
        const identity = deviceRef.current ?? (await getClientDeviceIdentity());
        if (generation !== requestGeneration.current) return;
        setUnavailableMessage(null);

        const response = await createPlaybackSession({
          deviceId: identity.deviceId,
          nowPlaying,
          recitalId,
          scope,
          mode,
          items,
          takeOver
        });
        if (generation !== requestGeneration.current) return;
        if (response.status === "stream-in-use") {
          setStreamInUseLabel(response.activeDeviceLabel);
          return;
        }
        setStreamInUseLabel(undefined);
        clearDisplacedListener();
        revokedRef.current = false;
        setRevoked(false);
        setDisplaced(false);
        setDisplacedSession(null);
        sessionIdRef.current = response.sessionId;
        setSessionId(response.sessionId);
        setReceiverBadge(response.receiverBadge ?? null);
        setAuthorizationStatus("ready");
        retryableSessionFailureRef.current = false;
        sessionRecoveryAttemptRef.current = 0;
        const latestNowPlaying = nowPlayingRef.current;
        publishedPlaybackActiveRef.current = {sessionId: response.sessionId, active: false};
        if (playbackActiveRef.current) {
          publishedPlaybackActiveRef.current = {sessionId: response.sessionId, active: true};
          void heartbeatPlaybackSession(response.sessionId, latestNowPlaying, true).catch(() => {
            publishedPlaybackActiveRef.current = null;
          });
        }
        setUrls((current) => ({ ...current, ...mapGrants(response.dances) }));
        setVideoSources((current) => ({
          ...current,
          ...mapVideoSources(response.dances)
        }));
        setStoryboardUrls((current) => ({ ...current, ...mapStoryboardGrants(response.dances) }));
        setGrantExpiries((current) => ({ ...current, ...mapGrantExpiries(response.dances) }));
        setCachedPlaybackGrants(response.dances);
        scheduleRefresh(response.sessionId, items, response.dances, generation);
        if (
          latestNowPlaying &&
          (latestNowPlaying.danceId !== nowPlaying.danceId ||
            latestNowPlaying.title !== nowPlaying.title)
        ) {
          void heartbeatPlaybackSession(response.sessionId, latestNowPlaying).catch(() => {
            // The regular heartbeat will retry the latest metadata.
          });
        }
      } catch (error) {
        if (generation !== requestGeneration.current) return;
        if (firebaseErrorCode(error) === "permission-denied") {
          retryableSessionFailureRef.current = false;
          setUnavailableMessage(firebaseErrorMessage(error));
          setAuthorizationStatus("error");
        } else if (firebaseErrorCode(error) === "failed-precondition") {
          retryableSessionFailureRef.current = false;
          setAuthorizationStatus("device-denied");
        } else {
          retryableSessionFailureRef.current = isRetryableSessionError(error);
          setUnavailableMessage(null);
          setAuthorizationStatus("error");
        }
      }
    },
    [
      authBootstrapComplete,
      clearDisplacedListener,
      clearRefresh,
      clearSessionRecovery,
      deviceActivationComplete,
      localPlaybackMode,
      scheduleRefresh
    ]
  );

  startQueueRef.current = startQueue;

  // Browser tabs commonly wake before WebKit/Chromium has restored their
  // network transport. Retry a transient session bootstrap with backoff, and
  // also retry immediately on every foreground/connectivity signal. The
  // active queue and route stay mounted throughout recovery.
  useEffect(() => {
    if (
      localPlaybackMode ||
      authorizationStatus !== "error" ||
      !retryableSessionFailureRef.current ||
      !activeRequest
    ) {
      clearSessionRecovery();
      return undefined;
    }

    const retry = () => {
      if (!retryableSessionFailureRef.current) return;
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        return;
      }
      clearSessionRecovery();
      void startQueueRef.current(activeRequestRef.current ?? activeRequest);
    };
    const retryWhenVisible = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        retry();
      }
    };
    const attempt = Math.min(
      sessionRecoveryAttemptRef.current,
      SESSION_RECOVERY_DELAYS_MS.length - 1
    );
    sessionRecoveryAttemptRef.current += 1;
    sessionRecoveryTimer.current = setTimeout(retry, SESSION_RECOVERY_DELAYS_MS[attempt]);

    if (typeof window !== "undefined") {
      window.addEventListener("focus", retry);
      window.addEventListener("online", retry);
      window.addEventListener("pageshow", retry);
      document.addEventListener("visibilitychange", retryWhenVisible);
    }
    return () => {
      clearSessionRecovery();
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", retry);
        window.removeEventListener("online", retry);
        window.removeEventListener("pageshow", retry);
        document.removeEventListener("visibilitychange", retryWhenVisible);
      }
    };
  }, [activeRequest, authorizationStatus, clearSessionRecovery, localPlaybackMode]);

  // The signed media URLs stay long-lived for uninterrupted playback and
  // casting. This separate short heartbeat is only the account's claim on its
  // one active stream, so an abandoned or crashed player self-clears quickly.
  useEffect(() => {
    if (localPlaybackMode || !sessionId) return;
    const generation = requestGeneration.current;
    let cancelled = false;

    const timer = setInterval(() => {
      void heartbeatPlaybackSession(
        sessionId,
        nowPlayingRef.current ?? undefined,
        playbackActiveRef.current
      )
        .then((response) => {
          if (cancelled || generation !== requestGeneration.current) return;
          if (response.status === "ok") {
            const now = Date.now();
            const lastSuccess = lastHeartbeatSuccessLogRef.current;
            if (
              __DEV__ &&
              (lastSuccess.sessionId !== sessionId ||
                now - lastSuccess.at >= HEARTBEAT_SUCCESS_LOG_INTERVAL_MS)
            ) {
              console.log(
                `[playback-session] heartbeat ok appState=${AppState.currentState}`
              );
              lastHeartbeatSuccessLogRef.current = { sessionId, at: now };
            }
            return;
          }

          if (__DEV__) {
            console.log(
              `[playback-session] heartbeat ${response.status} appState=${AppState.currentState}`
            );
          }

          sessionIdRef.current = null;
          setSessionId(null);
          setReceiverBadge(null);
          if (response.status === "session-revoked") {
            requestGeneration.current += 1;
            const revokedGeneration = requestGeneration.current;
            clearRefresh();
            setUrls({});
            setVideoSources({});
            setStoryboardUrls({});
            setGrantExpiries({});
            revokedRef.current = true;
            setRevoked(true);
            resolveDisplacedDevice(revokedGeneration);
            return;
          }

          // The app may have been backgrounded longer than the presence lease.
          // Reacquire quietly; if another device took over, startQueue surfaces
          // the existing stream-in-use dialog instead of leaving stale grants.
          const retryRequest = activeRequestRef.current;
          if (retryRequest) void startQueue(retryRequest);
        })
        .catch((error) => {
          if (__DEV__) {
            console.log(
              `[playback-session] heartbeat failed appState=${AppState.currentState}: ${firebaseErrorMessage(error)}`
            );
          }
          // A brief network loss must not interrupt current playback. A later
          // heartbeat either renews the lease or reacquires it after expiry.
        });
    }, PLAYBACK_SESSION_HEARTBEAT_SECONDS * 1000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [clearRefresh, localPlaybackMode, resolveDisplacedDevice, sessionId, startQueue]);

  const updateNowPlaying = useCallback(
    (nowPlaying: PlaybackNowPlaying) => {
      const previous = nowPlayingRef.current;
      if (previous?.danceId === nowPlaying.danceId && previous.title === nowPlaying.title) {
        return;
      }
      nowPlayingRef.current = nowPlaying;
      if (activeRequestRef.current) {
        activeRequestRef.current = { ...activeRequestRef.current, nowPlaying };
        setActiveRequest(activeRequestRef.current);
      }
      const activeSessionId = sessionIdRef.current;
      if (!activeSessionId || localPlaybackMode) return;
      void heartbeatPlaybackSession(activeSessionId, nowPlaying)
        .then((response) => {
          if (__DEV__) {
            console.log(
              `[playback-session] now-playing heartbeat ${response.status} appState=${AppState.currentState}`
            );
          }
        })
        .catch((error) => {
          if (__DEV__) {
            console.log(
              `[playback-session] now-playing heartbeat failed appState=${AppState.currentState}: ${firebaseErrorMessage(error)}`
            );
          }
          // The realtime revocation listener and regular heartbeat own recovery.
        });
    },
    [localPlaybackMode]
  );

  // Guard by request identity, not object reference: every startQueue call
  // creates a fresh request object, so a reference guard would re-fire forever
  // if the server keeps denying while the local limit flag reads false.
  const healedRetryKey = useRef<string | null>(null);
  useEffect(() => {
    if (authorizationStatus !== "device-denied" || deviceLimitReached || !activeRequest) return;
    const requestKey =
      `${activeRequest.queueIdentity}:` +
      activeRequest.items.map((item) => `${item.performanceId}/${item.danceId}`).join(",");
    if (healedRetryKey.current === requestKey) return;
    healedRetryKey.current = requestKey;
    void startQueue(activeRequest);
  }, [activeRequest, authorizationStatus, deviceLimitReached, startQueue]);

  const freshSourceForDance = useCallback(
    async (danceId: string): Promise<string | null> => {
      if (localPlaybackMode || !sessionId) return urls[danceId] ?? null;
      const item = activeRequestRef.current?.items.find(
        (entry) => entry.danceId === danceId
      );
      if (!item) return null;
      const generation = requestGeneration.current;
      try {
        const response = await refreshPlayback({ sessionId, items: [item] });
        if (generation !== requestGeneration.current) return null;
        if (response.status === "session-revoked") {
          requestGeneration.current += 1;
          const revokedGeneration = requestGeneration.current;
          clearRefresh();
          setUrls({});
          setVideoSources({});
          setStoryboardUrls({});
          setGrantExpiries({});
          sessionIdRef.current = null;
          setSessionId(null);
          setReceiverBadge(null);
          revokedRef.current = true;
          setRevoked(true);
          resolveDisplacedDevice(revokedGeneration);
          return null;
        }
        if (response.status === "session-expired") {
          setUrls({});
          setVideoSources({});
          setStoryboardUrls({});
          setGrantExpiries({});
          sessionIdRef.current = null;
          setSessionId(null);
          setReceiverBadge(null);
          return null;
        }
        const grant = response.dances.find((dance) => dance.danceId === danceId);
        if (!grant) return null;
        setUrls((current) => ({ ...current, ...mapGrants(response.dances) }));
        setVideoSources((current) => ({
          ...current,
          ...mapVideoSources(response.dances)
        }));
        setStoryboardUrls((current) => ({
          ...current,
          ...mapStoryboardGrants(response.dances)
        }));
        setGrantExpiries((current) => ({ ...current, ...mapGrantExpiries(response.dances) }));
        setCachedPlaybackGrants(response.dances);
        if (response.receiverBadge) setReceiverBadge(response.receiverBadge);
        return grant.playbackUrl;
      } catch (error) {
        if (firebaseErrorCode(error) === "permission-denied") {
          setUnavailableMessage(firebaseErrorMessage(error));
          return null;
        }
        throw error;
      }
    },
    [clearRefresh, localPlaybackMode, resolveDisplacedDevice, sessionId, urls]
  );

  useEffect(
    () => () => {
      clearRefresh();
      clearSessionRecovery();
      clearDisplacedListener();
      clearPlaybackInactiveTimer();
    },
    [clearDisplacedListener, clearPlaybackInactiveTimer, clearRefresh, clearSessionRecovery]
  );

  const takeOverCurrentPlayback = useCallback(async () => {
    const request = activeRequestRef.current;
    if (!request) return;
    await startQueue({ ...request, takeOver: true });
  }, [startQueue]);

  const value = useMemo<PlaybackSessionState>(
    () => ({
      authorizationStatus,
      displaced,
      displacedSession,
      expiresAtForDance: (danceId) => grantExpiries[danceId] ?? null,
      freshSourceForDance,
      reportPlaybackActivity,
      sourceForDance: (danceId) => urls[danceId] ?? null,
      videoSourcesForDance: (danceId) => videoSources[danceId] ?? null,
      storyboardForDance: (danceId) => storyboardUrls[danceId] ?? null,
      startQueue,
      stopQueue,
      takeOverCurrentPlayback,
      takingOver: displaced && authorizationStatus === "requesting",
      updateNowPlaying,
      unavailableMessage,
      revoked,
      receiverBadge,
      sessionId
    }),
    [authorizationStatus, displaced, displacedSession, freshSourceForDance, grantExpiries, receiverBadge, reportPlaybackActivity, revoked, sessionId, startQueue, stopQueue, storyboardUrls, takeOverCurrentPlayback, unavailableMessage, updateNowPlaying, urls, videoSources]
  );

  return (
    <PlaybackSessionContext.Provider value={value}>
      {children}
      <StreamTakeoverDialog
        visible={streamInUseLabel !== undefined}
        activeDeviceLabel={streamInUseLabel ?? null}
        onCancel={() => {
          setStreamInUseLabel(undefined);
          router.back();
        }}
        onWatchHere={() => {
          setStreamInUseLabel(undefined);
          if (activeRequest) void startQueue({ ...activeRequest, takeOver: true });
        }}
      />
    </PlaybackSessionContext.Provider>
  );
}

export function usePlaybackSession() {
  const context = useContext(PlaybackSessionContext);
  if (!context) throw new Error("usePlaybackSession requires PlaybackSessionProvider");
  return context;
}
